/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/scoring.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * The seven quality dimensions. A closed set: a string would let a typo create an eighth dimension
 * that scores nothing, appears in no total, and shows up in no report. `accuracy` is measurable by
 * no rule kind that exists — it means "matches reality", which needs a reference source. A tenant
 * weighting it will see it empty on every score, and `unmeasurable_dimensions` on the score says
 * so, because "you have not written accuracy rules" and "Lumiq cannot measure accuracy" are
 * different facts and only one is the customer's to fix.
 */
export type Dimension = 'completeness' | 'validity' | 'accuracy' | 'consistency' | 'uniqueness' | 'timeliness' | 'integrity';

export const DIMENSIONS: readonly Dimension[] = ['completeness', 'validity', 'accuracy', 'consistency', 'uniqueness', 'timeliness', 'integrity'];

export function isDimension(value: unknown): value is Dimension {
  return (['completeness', 'validity', 'accuracy', 'consistency', 'uniqueness', 'timeliness', 'integrity'] as readonly unknown[]).includes(value);
}

/**
 * A rule's severity, which is where the per-rule weight `w_r` comes from. It lives on the
 * *binding* rather than the rule version, so one version can be a warning in dev and a blocker in
 * production — and where several of an execution's bindings pin one version at different
 * severities, the highest wins.
 */
export type Severity = 'info' | 'warning' | 'error' | 'blocker';

export const SEVERITYS: readonly Severity[] = ['info', 'warning', 'error', 'blocker'];

export function isSeverity(value: unknown): value is Severity {
  return (['info', 'warning', 'error', 'blocker'] as readonly unknown[]).includes(value);
}

/**
 * What a score covered, on two axes. Required on every score, and that is structural: there is no
 * serialisation path in this contract that can emit a value without one. Ratios are `null` rather
 * than `0` when the denominator is zero. A dataset version with no columns has no coverage to
 * report, and reporting `0` would present a degenerate harvest result as a coverage problem the
 * customer could fix by writing rules.
 */
export interface Coverage {
  /**
   * Columns in the dataset **version the score is attributed to** — not the current schema.
   * Reading the current one would make yesterday's coverage change when a column is added today,
   * and a coverage figure that moves without a recomputation is unusable in a review.
   */
  readonly columns_total: number;
  /**
   * Distinct columns named by at least one enabled binding. Distinct, because two rules on one
   * column is one column covered — counting rule-column pairs would let ten rules on one field
   * read as full coverage of a ten-column table.
   */
  readonly columns_with_a_rule: number;
  /** `null` when the version has no columns. */
  readonly column_ratio: number | null;
  /** Dimensions this tenant weights above zero. The denominator that matters. */
  readonly dimensions_weighted: number;
  /**
   * Weighted dimensions that actually held evidence. A 96 on two of seven is a different object
   * from a 96 on all seven, and column coverage alone cannot distinguish them.
   */
  readonly dimensions_scored: number;
  readonly dimension_ratio: number | null;
}

export function isCoverage(value: unknown): value is Coverage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['columns_total'] === 'number' &&
    typeof candidate['columns_with_a_rule'] === 'number' &&
    (typeof candidate['column_ratio'] === 'number' || candidate['column_ratio'] === null) &&
    typeof candidate['dimensions_weighted'] === 'number' &&
    typeof candidate['dimensions_scored'] === 'number' &&
    (typeof candidate['dimension_ratio'] === 'number' || candidate['dimension_ratio'] === null)
  );
}

/**
 * One dimension's value, or the honest absence of one. `value` is `null` exactly when
 * `rules_counted` is zero — the case where `Σ(w_r)` is zero and the formula divides by zero.
 * `rules_counted` and `rules_without_evidence` sit beside the value because a 100 from one rule
 * and a 100 from forty are different facts that a value alone cannot tell apart.
 */
export interface DimensionScore {
  readonly dimension: Dimension;
  /** `null` means no evidence. It never means zero. */
  readonly value: number | null;
  /** This tenant's weight for the dimension. Zero means "does not count for us". */
  readonly weight: number;
  readonly rules_counted: number;
  /**
   * Rules attributed to this dimension that measured nothing — a failed task, or a table with no
   * rows. Carried rather than dropped: "forty rules ran and twelve measured nothing" is the
   * finding, and omitting the twelve would report perfect coverage over the twenty-eight.
   */
  readonly rules_without_evidence: number;
}

export function isDimensionScore(value: unknown): value is DimensionScore {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['completeness', 'validity', 'accuracy', 'consistency', 'uniqueness', 'timeliness', 'integrity'] as readonly unknown[]).includes(candidate['dimension']) &&
    (typeof candidate['value'] === 'number' || candidate['value'] === null) &&
    typeof candidate['weight'] === 'number' &&
    typeof candidate['rules_counted'] === 'number' &&
    typeof candidate['rules_without_evidence'] === 'number'
  );
}

/**
 * One rule version's share of one score. The decomposition, per rule. Both point fields are on the
 * **dataset's** 0–100 scale, not the dimension's, which is what makes `sum(points_contributed) ==
 * value` and `sum(points_lost) == 100 - value` hold over a flat list. A client can therefore rank
 * every rule in a score by what it cost without grouping by dimension and rescaling. `rule_name`
 * and `rule_label` are stored copies rather than joins. A rule renamed or deprecated later must
 * still render under the name it had when the score was computed — a decomposition whose wording
 * changes as rules are edited is not evidence.
 */
export interface Contribution {
  readonly rule_version_id: string;
  readonly rule_name: string;
  /**
   * The semver label, `1.2.0`. Two scores months apart under one rule name are only comparable if
   * the version behind each is on the record.
   */
  readonly rule_label: string;
  readonly dimension: Dimension;
  readonly severity: Severity;
  readonly weight: number;
  readonly pass_ratio: number;
  readonly points_contributed: number;
  /** What this rule cost, in points of the dataset's total. The column a review sorts by. */
  readonly points_lost: number;
  readonly rows_evaluated: number;
  readonly violation_count: number;
}

export function isContribution(value: unknown): value is Contribution {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['rule_version_id'] === 'string' &&
    typeof candidate['rule_name'] === 'string' &&
    typeof candidate['rule_label'] === 'string' &&
    (['completeness', 'validity', 'accuracy', 'consistency', 'uniqueness', 'timeliness', 'integrity'] as readonly unknown[]).includes(candidate['dimension']) &&
    (['info', 'warning', 'error', 'blocker'] as readonly unknown[]).includes(candidate['severity']) &&
    typeof candidate['weight'] === 'number' &&
    typeof candidate['pass_ratio'] === 'number' &&
    typeof candidate['points_contributed'] === 'number' &&
    typeof candidate['points_lost'] === 'number' &&
    typeof candidate['rows_evaluated'] === 'number' &&
    typeof candidate['violation_count'] === 'number'
  );
}

/**
 * A dataset's quality score, and everything needed to defend it. Four identifiers, because
 * reproducing a score needs all four and a score that cannot be reproduced cannot be defended: the
 * dataset **version** it measured, the execution that produced the evidence, that execution's
 * `plan_hash`, and the score model version that weighted it. Attribution to the version is what
 * stops a schema change rewriting history.
 */
export interface Score {
  readonly id: string;
  readonly dataset_id: string;
  /** The version this score measured. A rule change can never rewrite it. */
  readonly dataset_version_id: string;
  readonly qualified_name: string;
  readonly execution_id: string;
  /**
   * From `core.rules`. Two scores whose executions share a plan hash provably measured the same
   * work, so a difference between them is a data change rather than a configuration change.
   */
  readonly plan_hash: string;
  readonly score_model_id: string;
  readonly score_model_version: number;
  /**
   * `null` when no weighted dimension held evidence. **Not zero.** A consumer thresholding on
   * "below 70" must check for `null` first, or it will raise a data-quality alert caused by the
   * absence of any quality measurement.
   */
  readonly value: number | null;
  /**
   * All seven, always — so a score's shape is the same for every tenant and a client needs no
   * per-tenant branch.
   */
  readonly dimensions: readonly DimensionScore[];
  /**
   * Empty when `value` is `null`: with no total there is no deficit for a decomposition to
   * explain. Otherwise one entry per rule version that produced evidence.
   */
  readonly contributions: readonly Contribution[];
  readonly coverage: Coverage;
  /**
   * `false` when the execution was `partial` — it measured *some* of what it set out to. A partial
   * run is not refused, because discarding real measurements is its own dishonesty; it is marked,
   * so a consumer can decline to alert on it.
   */
  readonly evidence_complete: boolean;
  /**
   * Weighted dimensions that held no evidence. Only weighted ones: listing dimensions a tenant
   * deliberately excluded would report five false gaps, and five false gaps is how a real one
   * stops being read.
   */
  readonly dimensions_without_evidence: readonly Dimension[];
  /**
   * Weighted dimensions **no rule kind can measure** — `accuracy` today. This is the platform's
   * gap, not the tenant's, and a screen that cannot tell it from "you have not written rules yet"
   * sends the customer looking for a problem on their side.
   */
  readonly unmeasurable_dimensions: readonly Dimension[];
  /**
   * Rules that ran and moved no number, because their kind maps to no dimension — `custom_sql`,
   * whose author told the system nothing about what it measures. Counted rather than dropped:
   * without this, a custom rule failing every row would leave a perfect score and no trace.
   */
  readonly unattributed_rules: number;
  readonly computed_at: string;
}

export function isScore(value: unknown): value is Score {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['dataset_version_id'] === 'string' &&
    typeof candidate['qualified_name'] === 'string' &&
    typeof candidate['execution_id'] === 'string' &&
    typeof candidate['plan_hash'] === 'string' &&
    typeof candidate['score_model_id'] === 'string' &&
    typeof candidate['score_model_version'] === 'number' &&
    (typeof candidate['value'] === 'number' || candidate['value'] === null) &&
    Array.isArray(candidate['dimensions']) &&
    (candidate['dimensions'] as readonly unknown[]).every((item) => isDimensionScore(item)) &&
    Array.isArray(candidate['contributions']) &&
    (candidate['contributions'] as readonly unknown[]).every((item) => isContribution(item)) &&
    candidate['coverage'] !== undefined &&
    typeof candidate['evidence_complete'] === 'boolean' &&
    Array.isArray(candidate['dimensions_without_evidence']) &&
    (candidate['dimensions_without_evidence'] as readonly unknown[]).every((item) => isDimension(item)) &&
    Array.isArray(candidate['unmeasurable_dimensions']) &&
    (candidate['unmeasurable_dimensions'] as readonly unknown[]).every((item) => isDimension(item)) &&
    typeof candidate['unattributed_rules'] === 'number' &&
    typeof candidate['computed_at'] === 'string'
  );
}

/** One page of scores, with ADR-009's `projected_at`. */
export interface ScorePage {
  readonly items: readonly Score[];
  /**
   * Always null. One execution produces one score per dataset it covered, which is bounded by the
   * execution's own fan-out cap.
   */
  readonly next_cursor: string | null;
  /**
   * ADR-009. When the read model behind this response was last projected, or **null** for a direct
   * read, which means *current* — not unknown. Nullable since K21, and the change is a correctness
   * fix rather than a relaxation. This endpoint reads `scoring.score` directly and used to stamp
   * `now()`, which can never look stale: the one field designed to surface lag was the one field
   * guaranteed to hide it. Null is the honest answer until a score-trend projection exists.
   */
  readonly projected_at: string | null;
  /**
   * ADR-009 gives score trends 60 seconds. Carried even on a direct read so a client renders one
   * component either way. See `query.yaml#/components/schemas/PageMeta`.
   */
  readonly staleness_budget_seconds?: number;
}

export function isScorePage(value: unknown): value is ScorePage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isScore(item)) &&
    (typeof candidate['next_cursor'] === 'string' || candidate['next_cursor'] === null) &&
    (typeof candidate['projected_at'] === 'string' || candidate['projected_at'] === null)
  );
}

/**
 * A score as a list cell shows it: the value, the coverage it was measured against, and whether
 * the evidence behind it was complete. Never the decomposition — that is `Score`. **Coverage is
 * required, not optional.** `docs/design/SCORING.md` calls a score without its coverage the
 * product telling a lie: a 98 measured on 4% of columns is not a 98. The domain type behind this
 * refuses to hold a value without coverage, and this schema mirrors that so a client cannot
 * receive one.
 */
export interface ScoreSummary {
  readonly dataset_id: string;
  /**
   * `null` when nothing was measured — **not** zero. A 0 means every rule failed; `null` means no
   * rule produced evidence, and a client showing 0 would rank a dataset nobody has tested as the
   * worst in the estate.
   */
  readonly value: number | null;
  /**
   * Covered columns over total, or `null` for a schema with no columns. `null` rather than 0
   * there, because 0% would present a degenerate harvest as a coverage problem the customer could
   * fix by writing rules — and they cannot, there is nothing to write them against.
   */
  readonly coverage_ratio: number | null;
  readonly columns_total: number;
  /**
   * Sent alongside the ratio because "3 of 210 columns" is what a steward acts on; a bare 1.4% is
   * not. The ratio is sent too, so no client has to divide and none divides by zero.
   */
  readonly columns_with_a_rule: number;
  /**
   * False when the execution behind this score was PARTIAL. Surfaced in a list on purpose: it is
   * the difference between "scores 62" and "scores 62 on the part we could measure", and a table
   * hiding it lets an incomplete measurement sit unremarked beside complete ones.
   */
  readonly evidence_complete: boolean;
  /**
   * Whether this may be rendered as a score at all — true only with both a value and a coverage
   * ratio. Server-computed because whether a number may be shown as a score is a product rule, and
   * a client deriving it independently is one that will eventually derive it differently.
   */
  readonly defensible: boolean;
  readonly computed_at: string;
}

export function isScoreSummary(value: unknown): value is ScoreSummary {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    (typeof candidate['value'] === 'number' || candidate['value'] === null) &&
    (typeof candidate['coverage_ratio'] === 'number' || candidate['coverage_ratio'] === null) &&
    typeof candidate['columns_total'] === 'number' &&
    typeof candidate['columns_with_a_rule'] === 'number' &&
    typeof candidate['evidence_complete'] === 'boolean' &&
    typeof candidate['defensible'] === 'boolean' &&
    typeof candidate['computed_at'] === 'string'
  );
}

export interface ScoreSummaryPage {
  readonly items: readonly ScoreSummary[];
  readonly meta: Readonly<Record<string, unknown>>;
}

export function isScoreSummaryPage(value: unknown): value is ScoreSummaryPage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isScoreSummary(item)) &&
    candidate['meta'] !== undefined
  );
}

/**
 * A tenant's own definition of how rule results aggregate. Immutable and versioned: a weight
 * change alters the meaning of every score computed under it, so editing in place would silently
 * rewrite history — last quarter's 94 becoming an 88 with nothing to say whether the data got
 * worse or the model moved.
 */
export interface ScoreModel {
  readonly id: string;
  /**
   * A plain integer, not a semver. There is no such thing as a compatible change to a weight — any
   * change changes every number it produces.
   */
  readonly version: number;
  /**
   * All seven dimensions, always present. Zero and absent are the same value and are stored the
   * same way, so nobody later has to ask whether a tenant meant to exclude a dimension or forgot
   * to configure it.
   */
  readonly dimension_weights: Readonly<Record<string, number>>;
  /**
   * `w_r` by severity. Here rather than hard-coded in the platform because "a blocker costs eight
   * times an info" is an arbitrary choice, and an arbitrary choice a customer cannot argue with is
   * one they will be asked to defend to a regulator.
   */
  readonly severity_weights: Readonly<Record<string, number>>;
  readonly published_by: string;
  readonly published_at: string;
  readonly notes: string | null;
}

export function isScoreModel(value: unknown): value is ScoreModel {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['version'] === 'number' &&
    candidate['dimension_weights'] !== undefined &&
    candidate['severity_weights'] !== undefined &&
    typeof candidate['published_by'] === 'string' &&
    typeof candidate['published_at'] === 'string' &&
    (typeof candidate['notes'] === 'string' || candidate['notes'] === null)
  );
}

/** One computed score, reduced to what a chart needs. */
export interface TrendPoint {
  readonly score_id: string;
  readonly computed_at: string;
  /**
   * `null` for a run that measured nothing. The gap is the finding, and a chart must show it
   * rather than closing over it.
   */
  readonly value: number | null;
  /**
   * Travels with the value, because a rising line over falling coverage is a score improving
   * because it is checking less.
   */
  readonly column_coverage: number | null;
  readonly score_model_version: number;
  readonly evidence_complete: boolean;
}

export function isTrendPoint(value: unknown): value is TrendPoint {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['score_id'] === 'string' &&
    typeof candidate['computed_at'] === 'string' &&
    (typeof candidate['value'] === 'number' || candidate['value'] === null) &&
    (typeof candidate['column_coverage'] === 'number' || candidate['column_coverage'] === null) &&
    typeof candidate['score_model_version'] === 'number' &&
    typeof candidate['evidence_complete'] === 'boolean'
  );
}

/**
 * A dataset's series, and the same points already split at every model change. `segments` is the
 * rendering primitive rather than a convenience. A client that draws one path through `points`
 * presents a reweighting as a regression; one that draws a line per segment cannot.
 */
export interface Trend {
  readonly dataset_id: string;
  readonly points: readonly TrendPoint[];
  /** One inner array per contiguous run of points under a single score model version. */
  readonly segments: readonly (readonly TrendPoint[])[];
  /**
   * True when any two adjacent points were computed under different models. Deliberately not "more
   * than one version in the window": a backfill can produce 1 → 2 → 1, and every boundary in that
   * sequence is a discontinuity.
   */
  readonly crosses_a_model_change: boolean;
  /**
   * ADR-009. Null for a direct read, which means *current* — not unknown. Nullable since K21: a
   * trend is computed from `scoring.score` by an index scan, so it *is* current, and the `now()`
   * that used to be stamped here could never look stale.
   */
  readonly projected_at: string | null;
  /** ADR-009 gives score trends 60 seconds. See `query.yaml#/PageMeta`. */
  readonly staleness_budget_seconds?: number;
}

export function isTrend(value: unknown): value is Trend {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    Array.isArray(candidate['points']) &&
    (candidate['points'] as readonly unknown[]).every((item) => isTrendPoint(item)) &&
    Array.isArray(candidate['segments']) &&
    typeof candidate['crosses_a_model_change'] === 'boolean' &&
    (typeof candidate['projected_at'] === 'string' || candidate['projected_at'] === null)
  );
}

/**
 * One quality score for a whole tenant, and the movement behind it. Every figure here is derived
 * from scores the platform still holds, never from a stored aggregate — so tightening the
 * weighting re-derives history rather than leaving rows asserting a number the platform no longer
 * computes.
 */
export interface EstateSummary {
  /**
   * The covered-column-weighted mean over every scored dataset. **`null` means no dataset has been
   * scored**, not that the estate scores zero — zero is "everything failed" and null is "nobody
   * has looked", and a card rendering the first for the second is the most misleading thing a
   * front page can say.
   */
  readonly value: number | null;
  /**
   * Datasets with a defensible score — the denominator of the mean. Pair it with a dataset total
   * from `core.catalog` to render "412 of 517"; the difference is the estate nobody has measured,
   * which is a coverage gap rather than a quality one.
   */
  readonly scored: number;
  /**
   * The sum of the weights: covered columns behind the mean. On the wire because it is what makes
   * the figure auditable — a reader can tell whether an 87.4 is a claim about four columns or four
   * hundred thousand.
   */
  readonly columns_measured: number;
  /**
   * The same weighted mean as it stood at the start of the window. Null when the window holds no
   * scored dataset.
   */
  readonly previous_value: number | null;
  /**
   * `value - previous_value`. **Null when there is no baseline**, deliberately not 0.0: "the
   * estate has not moved" and "we have no earlier measurement" are different facts, and only the
   * first is a statement about quality.
   */
  readonly delta: number | null;
  /**
   * The period `previous_value` looks back over. Present so a reader is never comparing a figure
   * against an unstated window.
   */
  readonly window_days: number;
}

export function isEstateSummary(value: unknown): value is EstateSummary {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (typeof candidate['value'] === 'number' || candidate['value'] === null) &&
    typeof candidate['scored'] === 'number' &&
    typeof candidate['columns_measured'] === 'number' &&
    (typeof candidate['previous_value'] === 'number' || candidate['previous_value'] === null) &&
    (typeof candidate['delta'] === 'number' || candidate['delta'] === null) &&
    typeof candidate['window_days'] === 'number'
  );
}

/**
 * The estate's weighted mean on one day. `value` is never null: a day with nothing scored has no
 * point at all, because a gap is honest about an absence of measurement where a zero is a claim.
 */
export interface EstatePoint {
  readonly day: string;
  readonly value: number;
  /** Datasets behind this day's mean. Never zero — such a day is absent from the series. */
  readonly scored: number;
}

export function isEstatePoint(value: unknown): value is EstatePoint {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['day'] === 'string' &&
    typeof candidate['value'] === 'number' &&
    typeof candidate['scored'] === 'number'
  );
}

export interface EstateTrend {
  readonly items: readonly EstatePoint[];
  readonly meta: Readonly<Record<string, unknown>>;
}

export function isEstateTrend(value: unknown): value is EstateTrend {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isEstatePoint(item)) &&
    candidate['meta'] !== undefined
  );
}
